/**
 * What the asset card reads off a register row (`equipment-page-asset-card`, R9, R12, R16) — pure,
 * so the card's wording is pinned without rendering it.
 */
import type { OverrideRowItem } from '@repo/design-system';
import type { TwinAssetResponse } from '@repo/front-end-services';
import { isGenericCatalogueManufacturer, toDisplayManufacturer } from '@repo/front-end-utils';

const text = (value: string | null | undefined): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value.trim() : null;

/** *you wrote: LF treadmill · T95* — what the customer wrote, never changed here; null when nothing. */
export const youWrote = (row: TwinAssetResponse): string | null => {
  const model = text(row.model_raw);
  const parts = [text(row.manufacturer_raw), text(row.product_line_raw), model];
  if (!model) parts.push(text(row.category_raw));
  const written = parts.filter((part): part is string => part !== null);
  return written.length > 0 ? `you wrote: ${written.join(' · ')}` : null;
};

/** The row's product as the field shows it: *Life Fitness · Integrity 95T*, *Generic · Bench*. */
export const productLabel = (row: TwinAssetResponse): string | null => {
  const product = text(row.product);
  if (!row.reference_id || !product) return null;
  if (isGenericCatalogueManufacturer(row.manufacturer)) return `Generic · ${product}`;
  const maker = text(row.manufacturer);
  return maker ? `${toDisplayManufacturer(maker)} · ${product}` : product;
};

/** The eight folded fields, in frame 08's order (R16). Notes are their own fold and not counted. */
export const COST_AND_DATE_FIELDS = [
  'unit_cost',
  'residual_value',
  'currency',
  'install_date',
  'manufacture_date',
  'warranty_end_date',
  'contract_end_date',
  'condition',
] as const;
export type CostAndDateField = (typeof COST_AND_DATE_FIELDS)[number];

/** How many of the eight hold a value: *Costs and Dates · 3 of 8 filled*. */
export const filledCount = (row: TwinAssetResponse): number =>
  COST_AND_DATE_FIELDS.filter(field => {
    const value = row[field];
    return typeof value === 'number' || (typeof value === 'string' && value.trim() !== '');
  }).length;

export type OverrideKind = 'capacity' | 'model_3d';

export const OVERRIDE_LABELS: Record<OverrideKind, string> = {
  capacity: 'Capacity',
  model_3d: '3D model',
};

/** The two `*_source` columns that say *override*: frame 08's *1 override*. */
export const overrideCount = (row: TwinAssetResponse): number =>
  [row.capacity_source, row.model_3d_source].filter(source => source === 'override').length;

export const overrideAside = (row: TwinAssetResponse): string | undefined => {
  const n = overrideCount(row);
  if (n === 0) return undefined;
  return n === 1 ? '1 override' : `${n} overrides`;
};

interface OverrideReading {
  value: string | number | null | undefined;
  source: 'catalogue' | 'override' | null | undefined;
  /** The product's value, as the read served it; null or absent when it could not say. */
  product: string | number | null | undefined;
}

const reading = (row: TwinAssetResponse, kind: OverrideKind): OverrideReading => {
  switch (kind) {
    case 'capacity':
      return {
        value: row.capacity,
        source: row.capacity_source,
        product: row.catalogue_capacity,
      };
    case 'model_3d':
      return { value: row.model_3d, source: row.model_3d_source, product: row.catalogue_model_3d };
  }
};

export interface OverrideRowHandlers {
  onChange: (kind: OverrideKind) => void;
  onUseProducts: (kind: OverrideKind) => void;
  /** A write for this row is in flight: actions are disabled. */
  busy?: boolean;
  /** The row being edited, whose value the caller swaps for its editor. */
  editing?: OverrideKind | null;
  editor?: React.ReactNode;
}

/**
 * Frame 08's *From the product* rows (R12). The source line says in words what the butter wash
 * says in colour: *product*, *overridden · product is {value}*, or *overridden* alone where the
 * read served no product value — a null `catalogue_*` never proves the product states none (the
 * row type's own rule), so the card never says *the product has none*. A row with no product
 * reads *no product* and offers nothing to go back to.
 */
export const overrideRowsFor = (
  row: TwinAssetResponse,
  name: string,
  modelName: (file: string) => string,
  handlers: OverrideRowHandlers,
): OverrideRowItem[] => {
  const hasProduct = !!row.reference_id;
  const show = (kind: OverrideKind, value: string | number | null | undefined): string | null => {
    if (value === null || value === undefined || value === '') return null;
    return kind === 'capacity' ? String(value) : modelName(String(value));
  };
  return (['capacity', 'model_3d'] as const).map(kind => {
    const label = OVERRIDE_LABELS[kind];
    const { value, source, product } = reading(row, kind);
    const overridden = source === 'override';
    let sourceLine: string;
    if (!hasProduct) sourceLine = 'no product';
    else if (!overridden) sourceLine = 'product';
    else {
      const productValue = show(kind, product);
      sourceLine = productValue ? `overridden · product is ${productValue}` : 'overridden';
    }
    const lower = label.charAt(0).toLowerCase() + label.slice(1);
    let action: OverrideRowItem['action'];
    if (overridden && hasProduct) {
      action = {
        label: 'Use Product’s',
        onAction: () => handlers.onUseProducts(kind),
        disabled: handlers.busy,
        'aria-label': `Use the product’s ${kind === 'capacity' ? 'capacity' : lower} for ${name}`,
      };
    } else {
      action = {
        label: 'Change',
        onAction: () => handlers.onChange(kind),
        disabled: handlers.busy,
        'aria-label': `Change ${kind === 'capacity' ? 'capacity' : lower} for ${name}`,
      };
    }
    const editing = handlers.editing === kind;
    return {
      id: kind,
      label,
      value: editing ? handlers.editor : show(kind, value),
      source: sourceLine,
      overridden: overridden && hasProduct,
      action: editing ? undefined : action,
    };
  });
};
